import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useFireDrills } from "../../store/store";
import type { FireDrill } from "../../types/api";
import Modal from "../../components/modal/Modal";
import ViewFireDrill from "./ViewFireDrill";
import axiosInstance from "../../common/axiosInstance";
import { useAuth } from "../../auth/AuthProvider";

interface FireDrillCalendarProps {
  onEmailCalendar: (month: string, events: CalendarEvent[]) => void;
  dateRange?: [Date | null, Date | null];
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: FireDrill;
  isCompleted: boolean;
  type: "test_drill" | "evidence_date" | "next_drill";
}

const localizer = momentLocalizer(moment);

const FireDrillCalendar: React.FC<FireDrillCalendarProps> = ({
  onEmailCalendar,
  dateRange,
}) => {
  const { fireDrillsData } = useFireDrills();
  const [selectedDrill, setSelectedDrill] = useState<FireDrill | null>(null);
  const [isViewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [isEmailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const [recipientEmails, setRecipientEmails] = useState<string>("");
  const [reminderEmails, setReminderEmails] = useState<string>(
    localStorage.getItem("reminderEmails") || ""
  );
  const [emailError, setEmailError] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>(
    moment().format("MMMM YYYY")
  );

  const { roleData } = useAuth();

  const events: CalendarEvent[] = fireDrillsData
    .flatMap((drill): CalendarEvent[] => {
      const events: CalendarEvent[] = [];

      const testDrillDateStr =
        typeof drill.date_of_test_drills === "string"
          ? drill.date_of_test_drills
          : "";
      if (testDrillDateStr) {
        const [day, month, year] = testDrillDateStr.split("/");
        const drillDate = new Date(`${year}-${month}-${day}`);

        if (!isNaN(drillDate.getTime())) {
          const isCompleted = !!drill.evidence_closure_files?.length;

          events.push({
            id: `${drill.id}-test`,
            title: `${drill.premise} - ${
              isCompleted ? "Completed" : "Scheduled"
            }`,
            start: drillDate,
            end: drillDate,
            allDay: true,
            resource: drill,
            isCompleted,
            type: "test_drill",
          });
        }
      }

      const evidenceDateStr =
        typeof drill.date_of_evidence === "string"
          ? drill.date_of_evidence
          : "";
      if (evidenceDateStr) {
        const [day, month, year] = evidenceDateStr.split("/");
        const evidenceDate = new Date(`${year}-${month}-${day}`);

        if (!isNaN(evidenceDate.getTime())) {
          events.push({
            id: `${drill.id}-evidence`,
            title: `${drill.premise} - Evidence Submitted`,
            start: evidenceDate,
            end: evidenceDate,
            allDay: true,
            resource: drill,
            isCompleted: true,
            type: "evidence_date",
          });
        }
      }

      const nextDrillStr =
        typeof drill.next_drill_scheduled === "string"
          ? drill.next_drill_scheduled
          : "";
      if (nextDrillStr) {
        const [day, month, year] = nextDrillStr.split("/");
        const nextDrillDate = new Date(`${year}-${month}-${day}`);

        if (!isNaN(nextDrillDate.getTime())) {
          events.push({
            id: `${drill.id}-next`,
            title: `${drill.premise} - Next Drill`,
            start: nextDrillDate,
            end: nextDrillDate,
            allDay: true,
            resource: drill,
            isCompleted: false,
            type: "next_drill",
          });
        }
      }

      return events;
    })
    .filter((event): event is CalendarEvent => event !== null);

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedDrill(event.resource);
    setViewModalOpen(true);
  };

  const handleNavigate = (date: Date) => {
    const newMonth = moment(date).format("MMMM YYYY");
    setSelectedMonth(newMonth);
  };

  const handleOpenEmailModal = () => {
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    const emails = recipientEmails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);
    const reminders = reminderEmails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);

    if (emails.length === 0 && reminders.length === 0) {
      setEmailError("Please enter at least one valid email address.");
      return;
    }

    try {

      // Save reminder emails to localStorage
      localStorage.setItem("reminderEmails", reminderEmails);

      // Send calendar email if recipientEmails is provided
      if (emails.length > 0) {
        const emailContent = generateCalendarEmailContent(
          selectedMonth,
          events
        );
        const response = await axiosInstance.post(`/api/email`, {
            to: emails,
            cc: [],
            bcc: [],
            subject: emailContent.subject,
            body: emailContent.body,
            templateType: "generic",
            additionalVariables: [],
          });

        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to send email");
        }
      }

      setEmailModalOpen(false);
      setRecipientEmails("");
      setEmailError("");
      onEmailCalendar(selectedMonth, events);
    } catch (error) {
      const errorMsg = `Failed to send email: ${
        error instanceof Error ? error.message : "Unknown error"
      }`;
      setEmailError(errorMsg);
    }
  };

  const sendReminderEmail = async (
    event: CalendarEvent,
    recipients: string[]
  ) => {
    if (recipients.length === 0) return;

    const fireDrill = event.resource;
    const date = moment(event.start).format("DD/MM/YYYY");

    const emailContent = {
      subject: `Reminder: Fire Drill Scheduled - ${fireDrill.premise} on ${date}`,
      body: `
Fire Drill Reminder

A fire drill is scheduled in 48 hours:

- Region: ${fireDrill.region || "N/A"}
- Premise: ${fireDrill.premise || "N/A"}
- Date of Test/Drill: ${fireDrill.date_of_test_drills || "N/A"}
- Responsible Person: ${fireDrill.person_responsibility || "N/A"}
- Participation Rate: ${fireDrill.participation_rate || "N/A"}%
- Observations for Improvement: ${
        fireDrill.observation_identified_for_improvement || "N/A"
      }
- Actions to be Taken: ${fireDrill.actions || "N/A"}
- Closure Evidence: ${fireDrill.closure_evidence || "N/A"}
- Average Drill Duration: ${fireDrill.average_drill || "N/A"} minutes
- Evacuation Time Trend: ${fireDrill.evacuation_trend || "N/A"} minutes
- Date of Evidence: ${fireDrill.date_of_evidence || "N/A"}
- Next Drill Scheduled: ${fireDrill.next_drill_scheduled || "N/A"}

Please prepare accordingly.

Thank you,
Fire Drill Management Team
      `,
    };

    try {
      const response = await axiosInstance.post(`/api/email`, {
          to: recipients,
          cc: [],
          bcc: [],
          subject: emailContent.subject,
          body: emailContent.body,
          templateType: "generic",
          additionalVariables: [],
        });

      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to send reminder email");
      }
    } catch (error) {
      console.error(
        `Failed to send reminder email for ${fireDrill.premise}:`,
        error
      );
    }
  };

  const checkForReminders = () => {
    const reminderRecipients = reminderEmails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email);
    if (reminderRecipients.length === 0) return;

    const now = moment();
    const in48Hours = moment().add(48, "hours");

    events.forEach((event) => {
      if (event.type !== "test_drill" || event.isCompleted) return;

      const eventTime = moment(event.start);
      if (
        eventTime.isAfter(now) &&
        eventTime.isBefore(in48Hours) &&
        // Check if reminder hasn't been sent recently (e.g., within last 24 hours)
        !localStorage.getItem(
          `reminderSent_${event.id}_${event.start.toISOString()}`
        )
      ) {
        sendReminderEmail(event, reminderRecipients);
        // Mark reminder as sent to prevent duplicate emails
        localStorage.setItem(
          `reminderSent_${event.id}_${event.start.toISOString()}`,
          "true"
        );
      }
    });
  };

  useEffect(() => {
    // Check for reminders every 24 hours
    checkForReminders();
    const interval = setInterval(checkForReminders, 24 * 60 * 60 * 1000); // 24 hours
    return () => clearInterval(interval);
  }, [events, reminderEmails]);

  const generateCalendarEmailContent = (
    month: string,
    events: CalendarEvent[]
  ) => {
    const monthEvents = events.filter((event) =>
      moment(event.start).isSame(moment(month, "MMMM YYYY"), "month")
    );

    const eventDetails = monthEvents.length
      ? monthEvents
          .map((event) => {
            const fireDrill = event.resource;
            const date = moment(event.start).format("DD/MM/YYYY");
            const eventType =
              event.type === "evidence_date"
                ? "Evidence Submission"
                : event.type === "next_drill"
                ? "Next Scheduled Drill"
                : event.isCompleted
                ? "Completed Test Drill"
                : "Scheduled Test Drill";

            const fileAttachments = fireDrill.evidence_closure_files?.length
              ? fireDrill.evidence_closure_files
                  .map((file) => {
                    let fileUrl: string;
                    if (typeof file === "string") {
                      fileUrl = file;
                    } else if ("url" in file && typeof file.url === "string") {
                      fileUrl = file.url;
                    } else if (
                      "name" in file &&
                      typeof file.name === "string"
                    ) {
                      fileUrl = file.name;
                    } else {
                      fileUrl = "Unknown File";
                    }
                    const fileName = fileUrl.split("/").pop() || "Unknown File";
                    return `${fileName}: ${fileUrl}`;
                  })
                  .join("\n")
              : "No files attached";

            return `
${eventType} - ${fireDrill.premise} (${date})
- Region: ${fireDrill.region || "N/A"}
- Premise: ${fireDrill.premise || "N/A"}
- Date of Test/Drill: ${fireDrill.date_of_test_drills || "N/A"}
- Responsible Person: ${fireDrill.person_responsibility || "N/A"}
- Participation Rate: ${fireDrill.participation_rate || "N/A"}%
- Observations for Improvement: ${
              fireDrill.observation_identified_for_improvement || "N/A"
            }
- Actions to be Taken: ${fireDrill.actions || "N/A"}
- Closure Evidence: ${fireDrill.closure_evidence || "N/A"}
- Average Drill Duration: ${fireDrill.average_drill || "N/A"} minutes
- Evacuation Time Trend: ${fireDrill.evacuation_trend || "N/A"} minutes
- Date of Evidence: ${fireDrill.date_of_evidence || "N/A"}
- Next Drill Scheduled: ${fireDrill.next_drill_scheduled || "N/A"}
- Evidence Files:
${fileAttachments}
            `;
          })
          .join("\n----------------------------------------\n")
      : "No fire drills scheduled for this month.";

    return {
      subject: `Fire Drill Calendar - ${month}`,
      body: `
Fire Drill Calendar for ${month}

Events:
${eventDetails}

Thank you for reviewing the fire drill schedule.
      `,
    };
  };

  return (
    <div className="p-4">
      <div className="flex justify-between mb-4">
        <h3 className="text-xl font-semibold">Fire Drill Calendar</h3>
        {roleData?.roleName !== "perform-drill" && (
          <button
            className="bg-[#0fa9a6] text-white px-5 py-2 rounded-full font-semibold hover:bg-[#197f7d] transition-all focus:outline-none"
            onClick={handleOpenEmailModal}
          >
            Email Calendar
          </button>
        )}
      </div>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 500 }}
        onSelectEvent={handleSelectEvent}
        onNavigate={handleNavigate}
        views={["month", "week", "day"]}
        eventPropGetter={(event: CalendarEvent) => {
          let backgroundColor = "#3B82F6"; // Blue for current drills
          if (event.type === "evidence_date") {
            backgroundColor = "#8B5CF6"; // Purple for evidence dates
          } else if (event.type === "next_drill") {
            backgroundColor = "#F59E0B"; // Orange for next drills
          } else if (event.isCompleted) {
            backgroundColor = "#10B981"; // Green for completed test drills
          }

          return {
            style: { backgroundColor, color: "white" },
            title: (
              <span
                title={
                  event.type === "evidence_date"
                    ? "Evidence submission date"
                    : event.type === "next_drill"
                    ? "Next scheduled drill"
                    : event.isCompleted
                    ? "Completed with evidence"
                    : "Scheduled drill"
                }
              >
                {event.title}
              </span>
            ),
          };
        }}
      />
      <Modal
        isOpen={isViewModalOpen}
        closeButton={false}
        modalClass="bg-white w-full max-w-6xl max-h-[90vh] rounded-xl shadow-lg flex flex-col"
        onClose={() => setViewModalOpen(false)}
      >
        {selectedDrill && (
          <ViewFireDrill
            fireDrill={selectedDrill}
            onClose={() => setViewModalOpen(false)}
          />
        )}
      </Modal>
      <Modal
        isOpen={isEmailModalOpen}
        closeButton={true}
        modalClass="bg-white w-full max-w-md rounded-xl shadow-lg p-6"
        onClose={() => {
          setEmailModalOpen(false);
          setEmailError("");
          setRecipientEmails("");
        }}
      >
        <h2 className="text-lg font-semibold mb-4">Send Calendar Email</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Recipient Emails for Calendar (comma-separated)
          </label>
          <input
            type="text"
            value={recipientEmails}
            onChange={(e) => setRecipientEmails(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:border-teal-500"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Recipient Emails for 48-Hour Reminders (comma-separated)
          </label>
          <input
            type="text"
            value={reminderEmails}
            onChange={(e) => setReminderEmails(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:border-teal-500"
          />
          {emailError && (
            <p className="text-red-500 text-sm mt-1">{emailError}</p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button
            className="bg-gray-300 text-gray-800 px-4 py-2 rounded-full hover:bg-gray-400"
            onClick={() => {
              setEmailModalOpen(false);
              setEmailError("");
              setRecipientEmails("");
            }}
          >
            Cancel
          </button>
          <button
            className="bg-teal-600 text-white px-4 py-2 rounded-full hover:bg-teal-700"
            onClick={handleSendEmail}
          >
            Save & Send
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default FireDrillCalendar;
